import type { PlaybookSelection } from "@repo/app-types/entities/playbook";
import type {
  MultiSelectProps,
  MultiSelectSection,
} from "@repo/ui/Select/multiSelect";
import type { FC } from "react";
import { PlaybookAndVoiceAgentMultiSelect } from "../../../components/playbookAndVoiceAgentMultiSelect.js";
import {
  buildDynamicSelectionOptions,
  checkIsVoiceAgentOption,
  DYNAMIC_SELECTION_OPTIONS,
  isDynamicSelectionOptionId,
  usePlaybookAndVoiceAgentOptions,
} from "../../../hooks/usePlaybookAndVoiceAgentOptions.js";

/** Compact selection summary for places without access to playbook names. */
export function formatSignalPlaybookScopeLabel(
  selection: PlaybookSelection,
): string {
  if (selection.type === "playbooks") {
    return selection.ids.length === 1
      ? "1 playbook"
      : `${selection.ids.length} playbooks`;
  }
  return DYNAMIC_SELECTION_OPTIONS[selection.type].label;
}

export interface SignalPlaybookScopeFieldProps {
  value: PlaybookSelection;
  onChange: (value: PlaybookSelection) => void;
  /** Rendered size of the select trigger; defaults to MultiSelect's medium. */
  size?: MultiSelectProps["size"];
}

/**
 * Multi-select over the company's playbooks and voice agents, with mutually
 * exclusive dynamic options on top ("All playbooks and voice agents" / "All
 * playbooks" / "All voice agents"). An empty concrete selection collapses
 * back to "all".
 */
export const SignalPlaybookScopeField: FC<SignalPlaybookScopeFieldProps> = (
  props,
) => {
  const { value, onChange } = props;

  const { options, isLoading, hasLoadedAllOptions } =
    usePlaybookAndVoiceAgentOptions();

  const leadingSections: readonly MultiSelectSection[] = [
    {
      id: "dynamic",
      options: buildDynamicSelectionOptions(
        options.some(checkIsVoiceAgentOption),
      ),
    },
  ];

  return (
    <PlaybookAndVoiceAgentMultiSelect
      options={options}
      isLoading={isLoading}
      hasLoadedAllOptions={hasLoadedAllOptions}
      leadingSections={leadingSections}
      selectedPlaybookIds={value.type === "playbooks" ? value.ids : []}
      selectedLeadingOptionId={
        value.type === "playbooks" ? undefined : value.type
      }
      size={props.size}
      onChange={({ playbookIds, addedLeadingOptionId }) => {
        if (
          addedLeadingOptionId !== undefined &&
          isDynamicSelectionOptionId(addedLeadingOptionId)
        ) {
          onChange(DYNAMIC_SELECTION_OPTIONS[addedLeadingOptionId].selection);
          return;
        }
        onChange(
          playbookIds.length > 0
            ? { type: "playbooks", ids: [...playbookIds] }
            : DYNAMIC_SELECTION_OPTIONS.all.selection,
        );
      }}
    />
  );
};
